/**
 * API учебного контура (E4.5): экземпляры фиксированного сценария, их представления и две
 * учебные команды. Экземпляр видит только владелец; учебные данные и изменения не
 * пересекаются с рабочими.
 */

import { isTrainingId } from '@/lib/entityId';
import { api, ApiError } from './client';
import { RESOURCE_KINDS } from './workspaceTypes';
import type { ResourceRef } from './workspaceTypes';

export const TRAINING_SCENARIO = 'platform-v1';

/** Карта экземпляра: ключ объекта сценария → его ID train:<instance>:<kind>:<key> */
export interface TrainingManifest {
	instance_id: string;
	schema_version: 1;
	owner_company_id: string;
	owner_user_id: string;
	resources: Record<string, ResourceRef>;
	events: Record<string, string>;
	policies: Record<string, string>;
	actors: Record<string, string>;
	llm_events: { masked: string; blocked: string };
	recipients: Record<string, string>;
	llm_routes: Record<string, string>;
	llm_policies: Record<string, string>;
	regions: Record<string, string>;
	clusters: Record<string, string>;
	sensors: Record<string, string>;
	report_templates: Record<string, string>;
	groups: Record<string, string>;
	data_types: Record<string, string>;
	endpoints: Record<string, string>;
}

/** Совместимость фиксированного schema1 проверяется до открытия данных и построения маршрутов. */
function manifestOf(value: unknown, expected?: string): TrainingManifest {
	const fail = (): never => {
		throw new ApiError(
			409,
			'Incompatible training manifest',
			{ code: 'training_manifest_incompatible' },
			{ messageKey: 'common:training.incompatible' },
		);
	};
	if (!value || typeof value !== 'object') return fail();
	const raw = value as Record<string, unknown>;
	const instance = raw.instance_id;
	if (
		raw.schema_version !== 1 ||
		typeof instance !== 'string' ||
		(expected !== undefined && instance !== expected) ||
		!isTrainingId(`train:${instance}:resource:check`)
	)
		return fail();
	const maps = {
		events: 'event',
		policies: 'policy',
		actors: 'actor',
		recipients: 'recipient',
		llm_routes: 'llm_route',
		llm_policies: 'llm_policy',
		regions: 'region',
		clusters: 'cluster',
		sensors: 'sensor',
		report_templates: 'report_template',
		groups: 'group',
		data_types: 'data_type',
		endpoints: 'endpoint',
	} as const;
	for (const [field, kind] of Object.entries(maps)) {
		const entries = raw[field];
		if (!entries || typeof entries !== 'object' || Array.isArray(entries)) return fail();
		for (const [key, id] of Object.entries(entries))
			if (id !== `train:${instance}:${kind}:${key}` || !isTrainingId(String(id))) return fail();
	}
	const resources = raw.resources;
	if (!resources || typeof resources !== 'object' || Array.isArray(resources)) return fail();
	for (const [key, value] of Object.entries(resources)) {
		if (!value || typeof value !== 'object') return fail();
		const ref = value as Record<string, unknown>;
		if (
			!RESOURCE_KINDS.includes(ref.kind as (typeof RESOURCE_KINDS)[number]) ||
			ref.id !== `train:${instance}:${String(ref.kind)}:${key}` ||
			!isTrainingId(String(ref.id))
		)
			return fail();
	}
	const get = (field: string, key: string) => (raw[field] as Record<string, unknown>)[key];
	if (
		!get('resources', 'exports') ||
		!get('events', 'EV-1042') ||
		!get('policies', 'public-data') ||
		!get('recipients', 'siem') ||
		!get('recipients', 'email-soc') ||
		!get('recipients', 'external-api') ||
		!get('clusters', 'prod-cluster')
	)
		return fail();
	const llm = raw.llm_events as Record<string, unknown> | undefined;
	if (
		llm?.masked !== `train:${instance}:llm_event:llm-masked` ||
		llm.blocked !== `train:${instance}:llm_event:llm-blocked`
	)
		return fail();
	return value as TrainingManifest;
}

function snapshotOf(
	value: { manifest: unknown; version: number },
	instance: string,
): { manifest: TrainingManifest; version: number } {
	const manifest = manifestOf(value.manifest, instance);
	if (!Number.isSafeInteger(value.version) || value.version < 0)
		throw new ApiError(
			409,
			'Invalid training version',
			{ code: 'training_manifest_incompatible' },
			{ messageKey: 'common:training.incompatible' },
		);
	return { manifest, version: value.version };
}

/** Экраны учебного контура */
export const TRAINING_VIEWS = [
	'services',
	'endpoints',
	'data-types',
	'groups',
	'attack',
	'copilot',
	'storage',
	'overview',
	'resources',
	'resource',
	'map',
	'event',
	'policies',
	'llm',
	'sensors',
	'integrations',
	'reports',
] as const;
export type TrainingViewKind = (typeof TRAINING_VIEWS)[number];

/** Учебное представление: данные в форме соответствующего рабочего ответа */
export interface TrainingView<T = unknown> {
	kind: TrainingViewKind;
	subject: string;
	data: T;
}

/** Черновик учебной политики доступа -- та же форма, что у рабочей */
export interface AccessPolicyDraftInput {
	name: string;
	description: string;
	enabled: boolean;
	severity: string | null;
	scope: {
		resource_kinds: string[];
		resource_refs: ResourceRef[];
		group_ids: string[];
		data_type_ids: string[];
	};
	conditions: {
		code: string;
		actions: string[];
		allowed_principals: string[];
		allowed_group_ids: string[];
	}[];
}

/** Учебная команда: разбор события или сохранение учебной политики доступа */
export type TrainingCommand =
	| {
			kind: 'event.assign';
			request_id: string;
			expected_version: number;
			event_id: string;
			status: string;
			assignee_id: string | null;
	  }
	| {
			kind: 'policy.save';
			request_id: string;
			expected_version: number;
			id: string | null;
			draft: AccessPolicyDraftInput;
	  };

export function createTrainingInstance(requestId: string): Promise<TrainingManifest> {
	return api
		.post<unknown>('/v3/training/instances', {
			scenario: TRAINING_SCENARIO,
			request_id: requestId,
		})
		.then((value) => manifestOf(value));
}

/** Учебные примеры пользователя: последний использованный -- первым */
export function listTrainingInstances(): Promise<{
	instances: { instance_id: string; scenario: string; created_at: number; updated_at: number }[];
}> {
	return api.get('/v3/training/instances');
}

export function getTrainingInstance(
	instanceId: string,
): Promise<{ manifest: TrainingManifest; version: number }> {
	return api
		.get<{
			manifest: unknown;
			version: number;
		}>(`/v3/training/instances/${encodeURIComponent(instanceId)}`)
		.then((value) => snapshotOf(value, instanceId));
}

/**
 * Учебное представление экрана. Параметры -- запись или URLSearchParams: повторяющиеся ключи
 * (kinds=a&kinds=b каталога) передаются фасаду как есть
 */
export function getTrainingView<T>(
	instanceId: string,
	view: TrainingViewKind,
	subject: string,
	params: URLSearchParams | Record<string, string | number | boolean | undefined> = {},
): Promise<TrainingView<T>> {
	const search =
		params instanceof URLSearchParams
			? new URLSearchParams(params)
			: new URLSearchParams(
					Object.entries(params).flatMap(([key, value]) =>
						value === undefined ? [] : [[key, String(value)]],
					),
				);
	search.set('subject', subject);
	return api.get<TrainingView<T>>(
		`/v3/training/instances/${encodeURIComponent(instanceId)}/views/${view}?${search.toString()}`,
	);
}

export function sendTrainingCommand<T>(
	instanceId: string,
	command: TrainingCommand,
): Promise<{ version: number; view: TrainingView<T> }> {
	return api.post(`/v3/training/instances/${encodeURIComponent(instanceId)}/commands`, command);
}

export function resetTrainingInstance(
	instanceId: string,
): Promise<{ manifest: TrainingManifest; version: number }> {
	return api
		.post<{
			manifest: unknown;
			version: number;
		}>(`/v3/training/instances/${encodeURIComponent(instanceId)}/reset`, {})
		.then((value) => snapshotOf(value, instanceId));
}

export function deleteTrainingInstance(instanceId: string): Promise<void> {
	return api.delete<void>(`/v3/training/instances/${encodeURIComponent(instanceId)}`);
}
